import { Link } from "react-router-dom";
import { mailHref, telHref, useSiteConfig, whatsappHref } from "@/lib/site-config";
import logoUrl from "@/assets/logo-final.png";
import markUrl from "@/assets/hive-mark.svg";

// The five communities named in the client's feedback, in their order.
const FOOTER_COMMUNITIES: { slug: string; name: string }[] = [
  { slug: "business-bay", name: "Business Bay" },
  { slug: "damac-lagoons", name: "Damac Lagoons" },
  { slug: "jumeirah-village-circle", name: "Jumeirah Village Circle" },
  { slug: "tilal-al-ghaf", name: "Tilal Al Ghaf" },
  { slug: "dubai-harbour", name: "Dubai Harbour" },
];

export function Footer() {
  const year = new Date().getFullYear();
  const siteConfig = useSiteConfig();
  const phone = siteConfig.phone;
  const whatsapp = siteConfig.whatsapp;
  const email = siteConfig.email;
  const hours = siteConfig.hours;
  const reraOrn = siteConfig.reraOrn;

  return (
    // shrink-0 matters: combined with `overflow-hidden`, a flex child's
    // min-height floor drops to 0 and a height-constrained parent would
    // crush the footer flat.
    <footer
      data-on-dark
      className="relative shrink-0 overflow-hidden bg-[var(--navy-deep)] border-t border-[rgba(195,158,68,0.44)]"
    >
      {/* Main section — `relative` so the brand mark can be pinned to its
          bottom-right corner: flush with the footer's right edge and
          resting on the imprint strip's rule beneath, never crossing it. */}
      <div className="relative">
        {/* Brand mark — one large hexagon "H", tone-on-tone, fully inside
            the footer so no edge is clipped. The SVG is applied as a mask
            over a flat gold so the artwork's own gradient does not fight
            the low opacity. Height tracks this section from `md`; on
            phones the footer stacks tall, so the mark keeps a fixed size
            behind the contact column instead. */}
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-0 right-0 aspect-[324/366] h-[240px] bg-[var(--gold)] opacity-[0.16] sm:h-[300px] md:h-[min(92%,600px)]"
          style={{
            WebkitMaskImage: `url(${markUrl})`,
            WebkitMaskRepeat: "no-repeat",
            WebkitMaskSize: "contain",
            maskImage: `url(${markUrl})`,
            maskRepeat: "no-repeat",
            maskSize: "contain",
          }}
        />
        {/* Content sits in a narrower centred block (not the full-width
            container) so the columns cluster in the middle of the footer,
            per the client's reference. */}
        {/* lg switches from the 12-col grid to weighted fractions: the
            brand blurb and contact column get room to breathe while the
            two link lists stay compact. */}
        <div className="relative mx-auto grid w-full max-w-[1260px] grid-cols-2 gap-x-8 gap-y-10 px-5 pb-8 pt-10 sm:px-6 sm:gap-x-12 sm:pb-9 sm:pt-12 md:grid-cols-12 md:items-start md:px-10 lg:grid-cols-[1.5fr_0.7fr_0.85fr_1.35fr] lg:gap-x-10">
          {/* Brand column */}
          <div className="col-span-2 md:col-span-4 lg:col-span-1">
            <img
              src={logoUrl}
              alt="Hive Homes"
              width={600}
              height={355}
              className="h-10 w-auto sm:h-12"
            />
            <p className="mt-3 text-[16px] leading-snug text-[var(--cream)]">
              Turning Dreams into Addresses.
            </p>
            <p className="mt-4 max-w-[46ch] text-[13.5px] leading-[1.7] text-[var(--text-on-dark-soft)]">
              A boutique real estate brokerage operating across Dubai's most
              sought-after communities. We guide buyers, sellers, and investors
              to the right property and stay with them long after the deal is
              done.
            </p>
            {reraOrn?.set && (
              <p className="mt-4 text-[10.5px] uppercase tracking-[0.18em] text-[var(--text-on-dark-soft)]">
                RERA Registered · Licence No.{" "}
                <span className="text-[var(--cream)]">
                  {reraOrn.display}
                </span>
              </p>
            )}
          </div>

          {/* Navigate */}
          <FooterColumn label="Navigate" className="md:col-span-2 lg:col-span-1">
            <FooterLink to="/properties">Properties</FooterLink>
            <FooterLink to="/areas">Communities</FooterLink>
            <FooterLink to="/developers">Developers</FooterLink>
            <FooterLink to="/about">About</FooterLink>
            <FooterLink to="/insights">Insights</FooterLink>
            <FooterLink to="/contact">Contact</FooterLink>
            <FooterLink to="/list-property">List Your Property</FooterLink>
          </FooterColumn>

          {/* Communities */}
          <FooterColumn label="Communities" className="md:col-span-3 lg:col-span-1">
            {FOOTER_COMMUNITIES.map((c) => (
              <FooterLink key={c.slug} to={`/areas/${c.slug}`}>
                {c.name}
              </FooterLink>
            ))}
            <FooterLink to="/areas">All Communities</FooterLink>
          </FooterColumn>

          {/* Talk to us */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <FooterLabel>Talk to us</FooterLabel>
            {/* Per feedback: the mobile number for calls; WhatsApp lives as
                the logo icon leading the social row (wa.me redirect) rather
                than a duplicated number. The office landline keeps its own
                "Phone" row. */}
            <ul className="mt-3 flex flex-col gap-0 text-[13.5px] lg:gap-2">
              {phone?.set && (
                <li className="flex min-h-11 items-center gap-2 lg:min-h-0 lg:items-baseline">
                  <span className="w-[72px] flex-none text-[10.5px] uppercase tracking-[0.16em] text-[var(--text-on-dark-soft)]">
                    Phone
                  </span>
                  <a
                    href={telHref(phone)}
                    className="text-[var(--cream)] hover:text-[var(--cream-bright)] transition-colors"
                  >
                    {phone.display}
                  </a>
                </li>
              )}
              {whatsapp?.set && (
                <li className="flex min-h-11 items-center gap-2 lg:min-h-0 lg:items-baseline">
                  <span className="w-[72px] flex-none text-[10.5px] uppercase tracking-[0.16em] text-[var(--text-on-dark-soft)]">
                    Mobile
                  </span>
                  <a
                    href={telHref(whatsapp)}
                    className="text-[var(--cream)] hover:text-[var(--cream-bright)] transition-colors"
                  >
                    {whatsapp.display}
                  </a>
                </li>
              )}
              {email?.set && (
                <li>
                  <a
                    href={mailHref(email)}
                    className="inline-flex min-h-11 items-center break-all text-[var(--text-on-dark)] transition-colors hover:text-[var(--cream)] lg:min-h-0"
                  >
                    {email.display}
                  </a>
                </li>
              )}
              {hours?.set && (
                <li className="flex min-h-11 items-center text-[var(--text-on-dark-soft)] lg:min-h-0">
                  {hours.display}
                </li>
              )}
            </ul>

            <div className="mt-5">
              <Social config={siteConfig} />
            </div>
          </div>
        </div>
      </div>

      {/* Imprint strip */}
      <div className="relative border-t border-[rgba(240,234,216,0.14)]">
        <div className="mx-auto flex w-full max-w-[1260px] flex-col gap-3 px-5 py-5 text-[10.5px] tracking-[0.16em] uppercase text-[var(--text-on-dark-soft)] sm:px-6 md:flex-row md:items-center md:justify-between md:px-10">
          <p className="leading-relaxed">
            © {year} Hive Homes Real Estate Brokerage L.L.C. All rights
            reserved.
          </p>
          {/* The floating WhatsApp button occupies ~92px from the viewport's
              right edge. Between md and ~1600px the container runs close to
              full-bleed, so without this reserved padding the button sits on
              top of the "Terms of Use" link. */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 md:pr-16 min-[1600px]:pr-0">
            <Link to="/privacy" className="inline-flex min-h-11 items-center transition-colors hover:text-[var(--cream)] lg:min-h-0">
              Privacy Policy
            </Link>
            <Link to="/terms" className="inline-flex min-h-11 items-center transition-colors hover:text-[var(--cream)] lg:min-h-0">
              Terms of Use
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function Social({ config }: { config: ReturnType<typeof useSiteConfig> }) {
  const social = config.social;
  const items: { href: string; label: string; icon: React.ReactNode }[] = [];
  if (config.whatsapp?.set) {
    items.push({
      href: whatsappHref(config.whatsapp),
      label: "WhatsApp",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
        </svg>
      ),
    });
  }
  if (social?.instagram?.set) {
    items.push({
      href: socialHref(social.instagram.raw!, "instagram.com"),
      label: "Instagram",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
        </svg>
      ),
    });
  }
  if (social?.linkedin?.set) {
    items.push({
      href: socialHref(social.linkedin.raw!, "linkedin.com"),
      label: "LinkedIn",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
        </svg>
      ),
    });
  }
  if (social?.facebook?.set) {
    items.push({
      href: socialHref(social.facebook.raw!, "facebook.com"),
      label: "Facebook",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
    });
  }
  if (social?.tiktok?.set) {
    items.push({
      href: socialHref(social.tiktok.raw!, "tiktok.com"),
      label: "TikTok",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
        </svg>
      ),
    });
  }
  if (social?.youtube?.set) {
    items.push({
      href: socialHref(social.youtube.raw!, "youtube.com"),
      label: "YouTube",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      ),
    });
  }
  if (items.length === 0) return null;
  return (
    <span className="flex flex-wrap items-center gap-3 lg:gap-2">
      {items.map((it) => (
        <a
          key={it.label}
          href={it.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={it.label}
          className="focus-ring inline-flex size-11 items-center justify-center rounded-full border border-[rgba(240,234,216,0.22)] bg-[rgba(240,234,216,0.06)] text-[var(--cream)] transition-[background-color,border-color,color,transform] duration-200 hover:-translate-y-0.5 hover:border-[var(--gold-bright)] hover:bg-[var(--gold-bright)] hover:text-[var(--navy-deep)]"
        >
          {it.icon}
        </a>
      ))}
    </span>
  );
}

function socialHref(raw: string, host: string): string {
  if (/^https?:\/\//i.test(raw)) return raw;
  const trimmed = raw.trim();
  // TikTok and modern YouTube handles keep the leading @ in the URL path
  // (e.g. tiktok.com/@hivehomesrealestate, youtube.com/@HiveHomesRealEstate).
  if (host === "tiktok.com" || host === "youtube.com") {
    const handle = trimmed.startsWith("@") ? trimmed : `@${trimmed}`;
    return `https://${host}/${handle}`;
  }
  const handle = trimmed.replace(/^@/, "");
  return `https://${host}/${handle}`;
}

function FooterColumn({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <FooterLabel>{label}</FooterLabel>
      {/* Rows are 44px touch targets until `lg`, where the tighter
          desktop rhythm (28px rows, 8px gap) returns. */}
      <ul className="mt-3 flex flex-col gap-0 text-[13.5px] lg:gap-2">{children}</ul>
    </div>
  );
}

function FooterLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-bright)]">
      {children}
    </p>
  );
}

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <li>
      <Link
        to={to}
        className="inline-flex min-h-11 items-center text-[var(--text-on-dark)] transition-colors hover:text-[var(--cream)] lg:min-h-7"
      >
        {children}
      </Link>
    </li>
  );
}
